import useEnv from "./useEnv";
import finlandFlag from "../img/finland.png";
import swedenFlag from "../img/sweden.png";
import norwayFlag from "../img/norway.png";

const flagMap = {
    fi: finlandFlag,
    se: swedenFlag,
    no: norwayFlag
};

const ALLOWED_LANGS = ['fi', 'en', 'se', 'no'];
const ALLOWED_COUNTRIES = ['fi', 'se', 'no', 'global'];
const DEFAULT_LANG = 'en';
const DEFAULT_COUNTRY = 'fi';

/**
 * Hook to set up i18n
 * Locale is stored in session store and can be used to determine the current language.
 * Same goes for the current country.
 *
 * @returns {{
 *   getLanguages: (function(): string[]),
 *   getLocale: (function(): string),
 *   getLocaleBCP47: (function(): string),
 *   getCountry: (function(): string),
 *   setLocale: function(language: string)
 *   setupLanguage: (function(): string),
 *   setupCountry: function(),
 *   systemCountries: { locale: string, url: string, contactUrl: string }[],
 *   getLanguageLabel: (function(lang: string): string)
 * }}
 */
const useI18n = () => {
    const { getEnvJson, getEnv } = useEnv();

    /**
     * Sets up the language for the application
     *
     * @returns {string}
     */
    const setupLanguage = () => {
        let language = '';

        // If lang= URL parameter is present, use it as first priority.
        const urlLang = new URLSearchParams(window.location.search).get('lang');
        if (ALLOWED_LANGS.includes(urlLang)) {
            language = urlLang;
        }

        // If locale can be found from localStorage use it as a language.
        if (
            !ALLOWED_LANGS.includes((language)) &&
            localStorage.getItem('locale') !== null &&
            ALLOWED_LANGS.includes(localStorage.getItem('locale'))
        ) {
            language = localStorage.getItem('locale');
        }

        // If previous tries did not get determined locale, use browsers defaults.
        if (!ALLOWED_LANGS.includes(language)) {
            // Navigator.language returns 'en-gb' 'fi-en' etc.
            // Because this app has no full support for i18n/l10n functionalities, use only the first 'country' part of array split by '-'.
            language = (navigator.language ?? navigator.userLanguage).split('-')[0];
        }

        // If previous tries did not get determined locale, get locale from env.
        if (!ALLOWED_LANGS.includes(language)) {
            language = getEnv('LOCALE');
        }

        // If all previous tries did not get determined locale, fallback language to the default.
        if (!ALLOWED_LANGS.includes(language)) {
            language = DEFAULT_LANG;
        }

        localStorage.setItem('locale', language);
        return language;
    }

    /**
     * Sets up the country for the application
     */
    const setupCountry = () => {
        // user's country (locale) is determined by an environmental variable if possible
        let country = getEnv('LOCALE');

        // if TLD doesn't determine the country, use the fallback country
        if (!ALLOWED_COUNTRIES.includes(country)) {
            // fallback country 'fi' by default; affects e.g. the customer service url that will be displayed to the user in FeedbackDialog
            country = DEFAULT_COUNTRY;
        }

        localStorage.setItem('country', country);
    }

    /**
     * Sets the current locale for the application
     * @param {string} locale
     */
    const setLocale = (locale) => {
        localStorage.setItem('locale', locale);
    }

    /**
     * Gets the current locale for the application
     * @returns {string}
     */
    const getLocale = () => localStorage.getItem('locale') ?? DEFAULT_LANG;

    /**
     * Gets the current locale in BCP47 format
     * @returns {string}
     */
    const getLocaleBCP47 = () => {
        return mapLocaleToBCP47(getLocale());
    }

    /**
     * Gets the list of allowed languages for the application
     * @returns {string[]}
     */
    const getLanguages = () => ALLOWED_LANGS;

    /**
     * Gets the current country for the application
     * @returns {string}
     */
    const getCountry = () => localStorage.getItem('country') ?? DEFAULT_COUNTRY;

    /**
     * Maps the locale to BCP47 format
     * @param {string} locale
     * @returns {string}
     */
    const mapLocaleToBCP47 = (locale) => {
        const map = {
            'se': 'sv_SE',
            'en': 'en_GB',
            'no': 'nb_NO',
            'fi': 'fi_FI'
        }
        if (Object.keys(map).includes(locale)) {
            return map[locale];
        }
        return map[DEFAULT_LANG];
    }

    /**
     * Get label text for language (used in language selectors)
     * @param {string} lang
     * @returns {string}
     */
    const getLanguageLabel = (lang) => {
        if (!ALLOWED_LANGS.includes(lang)) {
            return '';
        }
        return {
            'se': 'Svenska',
            'en': 'English',
            'no': 'Norsk',
            'fi': 'Suomi'
        }[lang];
    }

    /**
     * System country options
     * @type {{ locale: string, url: string, contactUrl: string }[]}
     */
    const systemCountries = getEnvJson('SYSTEM_COUNTRIES') ?? [];

    /**
     * Returns the flag image for a given country locale, or null if not found.
     * @param {string} country
     * @returns {string|null}
     */
    const getCountryFlag = (country) => flagMap[country] ?? null;

    return {
        getCountry,
        getLocale,
        getLocaleBCP47,
        getLanguages,
        setLocale,
        setupLanguage,
        setupCountry,
        systemCountries,
        getLanguageLabel,
        getCountryFlag
    }
}

export default useI18n;
